import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeft,
  Plus,
  Send,
  FolderGit2,
  Sparkles,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';

// ---- Types ---------------------------------------------------------------

interface DevProject {
  pk: number;
  name: string;
  description?: string | null;
  type?: string;
  status?: string;
  open_request_count: number;
  awaiting_you_count: number;
  updated_at?: string | null;
}

interface DevMessage {
  pk: number;
  sender_type: 'customer' | 'staff' | 'ai' | 'system';
  body: string;
  created_at?: string;
}

interface DevRequest {
  pk: number;
  project_id: number;
  project_name?: string;
  title: string;
  body?: string | null;
  request_type: string;
  priority: string;
  status: string;
  triage_summary?: string | null;
  message_count?: number;
  updated_at?: string | null;
  messages?: DevMessage[];
}

// ---- Status presentation -------------------------------------------------

const AWAITING_YOU = ['needs_clarification', 'customer_review'];

const STATUS_META: Record<string, { label: string; cls: string }> = {
  triaging: { label: 'Reviewing', cls: 'bg-gray-100 text-gray-700' },
  needs_clarification: { label: 'Needs your answer', cls: 'bg-amber-100 text-amber-800' },
  ready: { label: 'Queued', cls: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In progress', cls: 'bg-indigo-100 text-indigo-700' },
  in_review: { label: 'In review', cls: 'bg-purple-100 text-purple-700' },
  customer_review: { label: 'Awaiting your approval', cls: 'bg-amber-100 text-amber-800' },
  done: { label: 'Done', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
};

const PRIORITY_CLS: Record<string, string> = {
  low: 'text-gray-500',
  medium: 'text-blue-600',
  high: 'text-orange-600',
  critical: 'text-red-600',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>{meta.label}</span>;
};

// ---- Component -----------------------------------------------------------

const SoftwareDevelopment: React.FC = () => {
  const api = apiService;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<DevProject[]>([]);

  const [activeProject, setActiveProject] = useState<DevProject | null>(null);
  const [requests, setRequests] = useState<DevRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const [activeRequest, setActiveRequest] = useState<DevRequest | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);

  // Composer state
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reply, setReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // ---- Loaders ----

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getDevProjects();
      const list: DevProject[] = res.result || [];
      setProjects(list);
      if (list.length === 1) {
        setActiveProject(list[0]);
      }
    } catch (e) {
      console.error('Failed to load dev projects', e);
      setError('Failed to load your software projects.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadRequests = useCallback(async (projectId: number) => {
    setRequestsLoading(true);
    try {
      const res = await api.getDevProjectRequests(projectId);
      setRequests(res.result || []);
    } catch (e) {
      console.error('Failed to load requests', e);
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [api]);

  const loadRequest = useCallback(async (requestId: number) => {
    setRequestLoading(true);
    try {
      const res = await api.getDevRequest(requestId);
      setActiveRequest(res.result || null);
    } catch (e) {
      console.error('Failed to load request', e);
    } finally {
      setRequestLoading(false);
    }
  }, [api]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { if (activeProject) loadRequests(activeProject.pk); }, [activeProject, loadRequests]);

  // Deep link: ?request=<id> (from an SMS/email notification) opens that request.
  useEffect(() => {
    const reqId = new URLSearchParams(window.location.search).get('request');
    if (!reqId) return;
    (async () => {
      try {
        const res = await api.getDevRequest(Number(reqId));
        const r: DevRequest | null = res.result || null;
        if (r) {
          setActiveProject({
            pk: r.project_id,
            name: r.project_name || 'Project',
            open_request_count: 0,
            awaiting_you_count: 0,
          });
          setActiveRequest(r);
        }
      } catch (e) {
        console.error('Failed to open linked request', e);
      }
    })();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Actions ----

  const submitNewRequest = async () => {
    if (!activeProject || !newTitle.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.createDevRequest(activeProject.pk, {
        title: newTitle.trim(),
        body: newBody.trim() || undefined,
      });
      const created: DevRequest = res.result;
      setShowNew(false);
      setNewTitle('');
      setNewBody('');
      await loadRequests(activeProject.pk);
      if (created?.pk) loadRequest(created.pk);
    } catch (e) {
      console.error('Failed to submit request', e);
      alert('Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!activeRequest || !reply.trim()) return;
    setSendingReply(true);
    try {
      await api.postDevRequestMessage(activeRequest.pk, reply.trim());
      setReply('');
      await loadRequest(activeRequest.pk);
      if (activeProject) loadRequests(activeProject.pk);
    } catch (e) {
      console.error('Failed to send reply', e);
      alert('Could not send your message. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  // ---- Render: loading / error ----

  if (loading) {
    return <div className="flex justify-center items-center h-64"><LoadingSpinner /></div>;
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  // ---- Render: request thread ----

  if (activeRequest) {
    return (
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => setActiveRequest(null)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to requests
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{activeRequest.title}</h1>
              <div className="mt-2 flex items-center gap-3 text-xs">
                <StatusBadge status={activeRequest.status} />
                <span className="text-gray-400 capitalize">{activeRequest.request_type}</span>
                <span className={`capitalize font-medium ${PRIORITY_CLS[activeRequest.priority] || 'text-gray-500'}`}>
                  {activeRequest.priority} priority
                </span>
              </div>
            </div>
          </div>

          {activeRequest.triage_summary && (
            <div className="mt-4 flex items-start gap-2 bg-violet-50 border border-violet-100 rounded-lg p-3 text-sm text-violet-900">
              <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0 text-violet-500" />
              <span>{activeRequest.triage_summary}</span>
            </div>
          )}

          {/* Thread */}
          <div className="mt-6 space-y-4">
            {requestLoading && <LoadingSpinner />}
            {(activeRequest.messages || []).map((m) => {
              const mine = m.sender_type === 'customer';
              const isAi = m.sender_type === 'ai';
              return (
                <div key={m.pk} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap
                    ${mine ? 'bg-violet-600 text-white'
                      : isAi ? 'bg-violet-50 text-violet-900 border border-violet-100'
                      : 'bg-gray-100 text-gray-800'}`}>
                    {!mine && (
                      <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">
                        {isAi ? 'AI Assistant' : m.sender_type === 'staff' ? 'ITP360 Team' : 'System'}
                      </div>
                    )}
                    {m.body}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reply composer */}
          <div className="mt-6 border-t border-gray-100 pt-4">
            <div className="flex items-end gap-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type a reply..."
                rows={2}
                className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-violet-500 focus:border-violet-500"
              />
              <button
                onClick={sendReply}
                disabled={sendingReply || !reply.trim()}
                className="inline-flex items-center justify-center rounded-lg bg-violet-600 text-white px-4 py-2 text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Render: project picker (>1 project, none selected) ----

  if (!activeProject) {
    return (
      <div className="max-w-5xl mx-auto">
        <Header />
        {projects.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <button
                key={p.pk}
                onClick={() => setActiveProject(p)}
                className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center text-white">
                    <FolderGit2 className="h-5 w-5" />
                  </div>
                  {p.awaiting_you_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                      <AlertCircle className="h-3 w-3" /> {p.awaiting_you_count} awaiting you
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-gray-900">{p.name}</h3>
                {p.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                <p className="text-xs text-gray-400 mt-3">{p.open_request_count} open request{p.open_request_count === 1 ? '' : 's'}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Render: request board for the active project ----

  return (
    <div className="max-w-4xl mx-auto">
      {projects.length > 1 && (
        <button
          onClick={() => setActiveProject(null)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> All projects
        </button>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{activeProject.name}</h1>
          {activeProject.description && <p className="text-gray-500 mt-1">{activeProject.description}</p>}
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center rounded-lg bg-violet-600 text-white px-4 py-2 text-sm font-medium hover:bg-violet-700"
        >
          <Plus className="h-4 w-4 mr-1" /> New request
        </button>
      </div>

      {/* New request composer */}
      {showNew && (
        <div className="bg-white border border-violet-200 rounded-xl p-5 mb-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3">Submit a request</h3>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Short summary (e.g. Add export button to reports page)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-3 focus:ring-violet-500 focus:border-violet-500"
          />
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="Describe what you need in as much detail as you like. Our AI will triage it instantly."
            rows={4}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm mb-3 focus:ring-violet-500 focus:border-violet-500"
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button
              onClick={submitNewRequest}
              disabled={submitting || !newTitle.trim()}
              className="inline-flex items-center rounded-lg bg-violet-600 text-white px-4 py-2 text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* Requests list */}
      {requestsLoading ? (
        <LoadingSpinner />
      ) : requests.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600">No requests yet. Submit your first change request above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <button
              key={r.pk}
              onClick={() => loadRequest(r.pk)}
              className={`w-full text-left bg-white border rounded-xl p-4 hover:shadow-md transition-all
                ${AWAITING_YOU.includes(r.status) ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-200'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{r.title}</h3>
                  {r.triage_summary && <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{r.triage_summary}</p>}
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                    <span className="capitalize">{r.request_type}</span>
                    <span className={`capitalize font-medium ${PRIORITY_CLS[r.priority] || ''}`}>{r.priority}</span>
                    {!!r.message_count && (
                      <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{r.message_count}</span>
                    )}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Small presentational helpers ----

const Header: React.FC = () => (
  <div className="mb-8">
    <h1 className="text-3xl font-bold text-gray-900">Software Development</h1>
    <p className="mt-2 text-gray-600">Submit change requests and track your project in real time.</p>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
    <FolderGit2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">Your project is being set up</h3>
    <p className="text-gray-600">We're getting your software project ready. Check back shortly or contact your ITP360 team.</p>
  </div>
);

export default SoftwareDevelopment;
